import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateService, Service, UpdateService } from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

export function useServices() {
  return useQuery({
    queryKey: qk.services,
    queryFn: async (): Promise<Service[]> => unwrap(await api.GET('/v1/services')),
  });
}

/** Chair time the booking occupies (duration + processing). Used to derive end times client-side. */
export function serviceSpanMin(service: Pick<Service, 'durationMin' | 'processingMin'>): number {
  return service.durationMin + service.processingMin;
}

export function useCreateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateService): Promise<Service> =>
      unwrap(await api.POST('/v1/services', { body: body as never })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.services }),
  });
}

export function useUpdateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: UpdateService }): Promise<Service> =>
      unwrap(
        await api.PATCH('/v1/services/{id}', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.services }),
  });
}
